import { LoaderFunctionArgs } from "@remix-run/cloudflare";
import { vercelStegaSplit } from "@vercel/stega";
import {
  AllSanitySchemaTypes,
  internalGroqTypeReferenceTo,
} from "~/sanity/types";

/** Strips zero-width chars and trailing whitespace (e.g. from Sanity preview/Stega). */
export function sanitizeDisplayText(text: string) {
  return text.replace(/[\u200B-\u200D\uFEFF]/g, "").trimEnd();
}

/**
 * Sanity Visual Editing / Presentation preview attaches Stega-encoded metadata to
 * string fields. Strip it before equality checks (e.g. state code → display title).
 */
export function stripSanityStegaFromString(value: string): string {
  return sanitizeDisplayText(vercelStegaSplit(value).cleaned);
}

/**
 * Removes leading and trailing slashes
 */
export function trimSlashes(string: string) {
  return string
    .split("/")
    .filter((string) => string !== "")
    .join("/");
}

export const getSlug = ({
  params,
}: {
  params: LoaderFunctionArgs["params"];
}) => {
  const { "*": pathname } = params;
  const path = trimSlashes([pathname].join("/"));
  const slug = path || "/";

  return slug;
};

// @link https://www.jacobparis.com/content/remix-pagination
export function setSearchParamsString(
  searchParams: URLSearchParams,
  changes: Record<string, string | number | undefined>
) {
  const newSearchParams = new URLSearchParams(searchParams);
  for (const [key, value] of Object.entries(changes)) {
    if (value === undefined) {
      newSearchParams.delete(key);
      continue;
    }
    newSearchParams.set(key, String(value));
  }
  // Print string manually to avoid over-encoding the URL
  // Browsers are ok with $ nowadays
  // optional: return newSearchParams.toString()
  return Array.from(newSearchParams.entries())
    .map(([key, value]) =>
      value ? `${key}=${encodeURIComponent(value)}` : key
    )
    .join("&");
}

export const getSearchParamPage = (searchParams: URLSearchParams) => {
  const page = Number(searchParams.get("page")) || 1;

  return page - 1;
};

export const getSearchParamYear = (searchParams: URLSearchParams) => {
  let paramYear = searchParams.get("year");
  let year = paramYear ? Number(paramYear) : null;

  return year;
};

export const getSearchParamRegion = (searchParams: URLSearchParams) => {
  let paramRegion = searchParams.get("region");
  let region = paramRegion ? paramRegion : null;

  return region;
};

export const getSearchParamState = (searchParams: URLSearchParams) => {
  let paramState = searchParams.get("state");
  let state = paramState ? paramState : null;

  return state;
};

// Expand references in a type
// @link https://github.com/sanity-io/sanity/issues/6327
type ExpandReference<T> = T extends {
  _ref: string;
  _type: "reference";
  [internalGroqTypeReferenceTo]?: infer R;
}
  ? R extends string
    ? Extract<AllSanitySchemaTypes, { _type: R }>
    : T
  : T;

export type ExpandReferences<T> = T extends object
  ? {
      [K in keyof T]: ExpandReference<ExpandReferences<T[K]>>;
    }
  : T;
